import _ from 'lodash';
import React, { ChangeEvent } from 'react';
import { 
  ASYNC_UPLOAD_DISPATCHER_NAME,
  Position
} from '../..';
import { Component } from '../../dispatcher/modules';
import Container from '../layout/container/Container';
import PuzzleButton from '../UI/button/PuzzleButton';
import { DRAG_AND_DROP_AREA_PROPS, UPLOAD_CONFIG } from './Types';

class DragAndDropArea extends Component<DRAG_AND_DROP_AREA_PROPS> {
  private filesInput: HTMLInputElement | null;

  static defaultProps = {
    allowUpload: true,
    concurrently: false,
    dragging: false,
    extractArchive: {
      value: false,
      enabled: false
    },
    uploadedFilesLength: 0,
    dragAndDropLabels: {}
  };

  /**
   * Drag leave event handler
   * @param event
   */
  onDragLeaveHandler = (event: React.DragEvent) => {
    if (_.isFunction(this.props.onDragLeave)) {
      this.props.onDragLeave(event);
    }
  };

  /**
   * Prevents default behavior so files are present on drop
   * @param event
   */
  maskedDragOverHandler = (event: React.DragEvent) => {
    event.preventDefault();
  };

  /**
   * Drop event handler handler
   * @param e
   */
  onDropHandler = (e: React.DragEvent) => {
    e.preventDefault();
    if (this.props.allowUpload) {
      const { files } = e.dataTransfer;
      let proceedWithUpload: boolean = true;
      if (_.isFunction(this.props.onDropHandler)) {
        proceedWithUpload = this.props.onDropHandler(e);
      }
      if (proceedWithUpload) {
        this.uploadFiles(files);
      }
    } else {
      this.onDragLeaveHandler(null);
      this.dispatch('fileUploadDisabledWarning', Position.end);
    }
  };

  /**
   * Renders content while dragging is in progress.
   */
  renderOnDragContent = () => (
    <div
      className={'tp-drag-and-drop-dropwrap'}
      onDragLeave={(e: any) => this.onDragLeaveHandler(e)}
      onDrop={(e: any) => this.onDropHandler(e)}
      onDragOver={(e: any) => this.maskedDragOverHandler(e)}
    >
      <Container
        justifyContentCenter={true}
        alignItemsCenter={true}
        className='tp-drag-inner-wrap tp-pointer-events-none'
        hBox={true}
      >
        <div className='tp-icon tp-custom-upload tp-pointer-events-none' />
        <div
          className='tp-drag-text tp-text-semibold tp-font-size-md tp-pointer-events-none'
        >
          {this.translate('dragAndDropText')}
        </div>
      </Container>
    </div>
  );

  /**
   * Triggers click event on file input field.
   */
  public fileUploadHandler = () => {
    this.filesInput.click();
  };

  /**
   * Handles upload of files selected through the file input.
   * @param e
   */
  fileSelectHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.currentTarget;
    // @ts-ignore
    e.dataTransfer = {
      files
    };
    this.onDropHandler(e as any);
  };

  /**
   * Return value for accept
   * @param { any } fileExtension
   */
  handleAccept = (fileExtension?: string[]) => {
    if (fileExtension) {
      const newFileExtension = fileExtension.map((item: string) => `.${item.replace('.', '')}`);
      return newFileExtension.join(', ');
    } else {
      return '';
    }
  }

  /**
   * Check if file have valid extension
   * @param { any[] } file
   */
  validFileExtension = (file: any[]) => {
    const fileExtensions = this.props.fileExtension?.map((item: string) => item.replace('.', ''));
    let isValid = true;

    file.forEach((item: any) => {
      if (isValid) {
        const fileExtensionArray = item.name.split('.');
        const fileExtensionName = fileExtensionArray[fileExtensionArray.length - 1];
        isValid = fileExtensions?.includes(fileExtensionName.toLowerCase());
      }
    });

    return isValid;
  }

  /**
   * Check if number of file is valid
   * @param { any[] } file
   */
  validFileNumber = (file: any[]) => !(file.length > 1 && this.props.multipleUpload === false)

  /**
   * Upload files handler.
   * @param files
   */
  uploadFiles = (files?: FileList) => {
    const {
      concurrently,
      extractArchive,
      uploadPanelModuleId,
      uploadConfig,
      fileExtension
    } = this.props as DRAG_AND_DROP_AREA_PROPS;
    const { isUpload = false, prepareRequest } = uploadConfig as UPLOAD_CONFIG;
    const isRequestWithoutPrepare = !isUpload && !prepareRequest;

    const actionName: string = isUpload ? 'uploadFilesRequestSimple' : isRequestWithoutPrepare ? 'dropUploadFilesWithoutPrepare' : 'dropUploadFiles';

    let params: any = {
      extractArchive: extractArchive.value
    };
    let file: File[] = Array.from(files || []);
    if (isUpload || isRequestWithoutPrepare) {
      params.file = file;
    }    

    if (fileExtension && !this.validFileExtension(file)) {
      this.dispatch('fileUploadDisabledWarningByInstructions', Position.end, {
        instructions: this.translate('fileExtensionWarning'),
        uploadPanelModuleId
      });
    } else if (!this.validFileNumber(file)) {
      this.dispatch('fileUploadDisabledWarningByInstructions', Position.end, {
        instructions: this.translate('singleFileUploadWarning'),
        uploadPanelModuleId
      });
    } else {
      this.dispatch(actionName, {
        dispatcherId: ASYNC_UPLOAD_DISPATCHER_NAME,
        pos: Position.end
      }, {
        ...uploadConfig,
        concurrently,
        uploadPanelModuleId,
        uploadGroupId: uploadPanelModuleId,
        droppedFiles: file,
        params,
        uploadParams: {
          file
        }
      });
    }
  };
  /**
   * Content that is rendered when dragging is not happening.
   */
  renderBeforeOnDragContent = () => {
    const { dragging, allowUpload, uploadedFilesLength, fileExtension, multipleUpload, dragAndDropLabels } = this.props as DRAG_AND_DROP_AREA_PROPS;
    const { dragAndDropText, browseButton } = dragAndDropLabels;
    const commonConfiguration: any = {
      alignItemsCenter: true,
      hBox: true
    };
    const inputId = [this.moduleName, this.moduleId, 'DragAndDropArea'].join('_');
    return (
      <>
        <input
          hidden={true}
          type="file"
          ref={ref => this.filesInput = ref}
          className="tp-file-input"
          multiple={_.isUndefined(multipleUpload) ? true : multipleUpload}
          id={inputId}
          accept={this.handleAccept(fileExtension)}
          onChange={this.fileSelectHandler}
          onClick={(event: any) => {
            if (event?.target?.value) {
              event.target.value = null;
            }
          }}
        />
        {!uploadedFilesLength ? <Container
          alignItemsCenter={true}
          className={'tp-drag-and-drop-placeholder-wrap tp-drag-and-drop'}
        >
          <label
            htmlFor={inputId}
            className="sr-only"
          >{this.translate('uploadLabel')}</label>
          {
            <Container
              {...commonConfiguration}
              justifyContentCenter={true}
              className={'tp-drag-and-drop-placeholder'}
            >
              <Container
                {...commonConfiguration}
                wrap={true}
              >
                <Container
                  alignItemsCenter
                  vBox
                  gap={4}
                  justifyContentCenter
                  className={`${dragging && 'tp-display-none'}`}
                >
                  <span className={'tp-drag-and-drop-cloud'} />
                  <Container
                    {...commonConfiguration}
                    className='tp-drag-and-drop-text-wrap'
                    wrap={true}
                  >
                    <div className={'tp-drag-and-drop-text'}>
                      {dragAndDropText ?? this.translate('dragAndDropText')}
                    </div>
                  </Container>
                  <PuzzleButton
                    // <debug>
                    data-cy={'browse-button'}
                    // </debug>
                    themeColor={'primary'}
                    fillMode="outline"
                    disabled={!allowUpload}
                    onClick={this.fileUploadHandler}
                    className="tp-drag-and-drop-browse-button k-button k-primary k-flat k-button-link k-small"
                  >
                    {browseButton ?? this.translate('browseButton')}
                  </PuzzleButton>
                  {_.isFunction(this.props.renderExtractCheckBox) ? this.props.renderExtractCheckBox() : null}
                </Container>
              </Container>
            </Container>
          }
        </Container> : null}
      </>
    );
  };

  renderBody(): any {
    return this.props.dragging ? this.renderOnDragContent() : this.renderBeforeOnDragContent();
  }
}

export default DragAndDropArea;
